import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Users, CheckCircle2, XCircle, UtensilsCrossed, Download, LogOut } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { getStats, getRSVPs, exportURL } from "../lib/api";

const STORAGE_KEY = "aa_admin_token";

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-deep-black/10 bg-ivory p-6" data-testid={`stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <Icon className="mb-4 h-5 w-5 text-gold-soft" strokeWidth={1.4} />
      <p className="font-serif text-5xl font-light text-deep-black">{value}</p>
      <p className="mt-2 text-xs uppercase tracking-[0.25em] text-deep-black/50">{label}</p>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [input, setInput] = useState("");
  const [authed, setAuthed] = useState(false);
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");

  const load = useCallback(async (tk) => {
    try {
      const [s, r] = await Promise.all([getStats(tk), getRSVPs(tk)]);
      setStats(s.data);
      setRows(r.data);
      setAuthed(true);
      setError("");
      localStorage.setItem(STORAGE_KEY, tk);
    } catch (e) {
      setError("Token de acceso inválido.");
      setAuthed(false);
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const exportCsv = async () => {
    const res = await axios.get(exportURL(), {
      responseType: "blob",
      headers: { "X-Admin-Token": token },
    });
    const url = URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = "wedding_rsvps.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setToken("");
    setAuthed(false);
    setStats(null);
    setRows([]);
  };

  if (!authed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-deep-black px-6">
        <div className="w-full max-w-sm rounded-2xl border border-ivory/10 bg-charcoal p-10 text-center">
          <p className="mb-2 text-xs uppercase tracking-[0.4em] text-gold">Administración</p>
          <h1 className="mb-8 font-serif text-3xl font-light text-ivory">Panel de Invitados</h1>
          <Input
            data-testid="admin-token-input"
            type="password"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && setToken(input)}
            placeholder="Token de acceso"
            className="mb-4 border-ivory/20 bg-transparent text-center text-ivory placeholder:text-ivory/30"
          />
          {error && <p className="mb-4 text-xs text-red-400" data-testid="admin-error">{error}</p>}
          <Button
            data-testid="admin-login-button"
            onClick={() => setToken(input)}
            className="w-full rounded-full bg-gold py-6 text-xs uppercase tracking-[0.2em] text-deep-black hover:bg-gold-soft"
          >
            Entrar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-warm-white px-6 py-14 md:px-12" data-testid="admin-dashboard">
      <div className="mx-auto max-w-6xl">
        <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.4em] text-gold-soft">Alexander &amp; Sofía</p>
            <h1 className="font-serif text-4xl font-light text-deep-black md:text-5xl">Panel de Confirmaciones</h1>
          </div>
          <div className="flex gap-3">
            <Button data-testid="export-csv-button" onClick={exportCsv} variant="outline" className="rounded-full border-deep-black/20">
              <Download className="mr-2 h-4 w-4" /> Exportar CSV
            </Button>
            <Button data-testid="admin-logout-button" onClick={logout} variant="ghost" className="rounded-full">
              <LogOut className="mr-2 h-4 w-4" /> Cerrar sesión
            </Button>
          </div>
        </div>

        <div className="mb-12 grid grid-cols-2 gap-4 md:grid-cols-4">
          <StatCard icon={CheckCircle2} label="Asistirán" value={stats?.attending_responses ?? 0} />
          <StatCard icon={XCircle} label="No podrán" value={stats?.declined_responses ?? 0} />
          <StatCard icon={Users} label="Invitados totales" value={stats?.total_guests_attending ?? 0} />
          <StatCard icon={UtensilsCrossed} label="Notas alimenticias" value={stats?.with_dietary ?? 0} />
        </div>

        <div className="overflow-hidden rounded-2xl border border-deep-black/10 bg-ivory">
          <Table data-testid="guest-table">
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Invitados</TableHead>
                <TableHead>Alimenticio</TableHead>
                <TableHead>Mensaje</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="py-12 text-center text-deep-black/40">
                    Aún no hay respuestas.
                  </TableCell>
                </TableRow>
              )}
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`guest-row-${r.id}`}>
                  <TableCell className="font-medium">{r.name}</TableCell>
                  <TableCell>
                    <span className={`rounded-full px-3 py-1 text-xs ${r.attending ? "bg-gold/20 text-gold-soft" : "bg-deep-black/10 text-deep-black/60"}`}>
                      {r.attending ? "Asistirá" : "No podrá"}
                    </span>
                  </TableCell>
                  <TableCell>{r.attending ? r.guests : "—"}</TableCell>
                  <TableCell className="max-w-[160px] truncate text-deep-black/70">{r.dietary || "—"}</TableCell>
                  <TableCell className="max-w-[240px] truncate text-deep-black/70">{r.message || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
